/**
 * Security Posture Service
 * 
 * Provides discoverable security evidence artifacts.
 * Makes M2.J2 (Security posture is discoverable and reviewable) true.
 * 
 * Evidence criteria:
 * - Security posture is presented as observable artifacts
 * - Customer can identify the right security decision owner
 * - Acceptance does not require ad hoc trust exceptions
 */

// ============================================================================
// Types
// ============================================================================

export type SecurityCategory = 
  | 'authentication'
  | 'authorization'
  | 'data_protection'
  | 'infrastructure'
  | 'compliance'
  | 'audit'
  | 'availability';

export type ComplianceStandard = 
  | 'SOC2_Type1'
  | 'SOC2_Type2'
  | 'GDPR'
  | 'CCPA'
  | 'HIPAA'
  | 'ISO27001';

export type EvidenceStatus = 'current' | 'pending' | 'not_applicable';

export interface SecurityControl {
  id: string;
  category: SecurityCategory;
  name: string;
  description: string;
  implementation: string;
  evidenceType: 'document' | 'configuration' | 'process' | 'technical';
  status: EvidenceStatus;
  lastVerified?: string;
  relatedCompliance?: ComplianceStandard[];
}

export interface SecurityPosture {
  version: string;
  lastUpdated: string;
  overallStatus: 'strong' | 'acceptable' | 'needs_improvement';
  controls: SecurityControl[];
  complianceStatus: Record<ComplianceStandard, EvidenceStatus>;
  contacts: SecurityContact[];
}

export interface SecurityContact {
  role: string;
  name: string;
  email: string;
  responsibility: string;
}

// ============================================================================
// Security Controls Definition
// ============================================================================

export const SECURITY_CONTROLS: SecurityControl[] = [
  // Authentication
  {
    id: 'auth-001',
    category: 'authentication',
    name: 'Identity Provider Integration',
    description: 'User authentication via Supabase Auth',
    implementation: 'Supabase Auth (email/password + JWT sessions) for all users',
    evidenceType: 'technical',
    status: 'current',
    lastVerified: '2026-06-15',
    relatedCompliance: ['SOC2_Type2', 'ISO27001'],
  },
  {
    id: 'auth-002',
    category: 'authentication',
    name: 'Session Management',
    description: 'Secure session handling with automatic expiration',
    implementation: 'JWT-based sessions with configurable expiration and refresh tokens',
    evidenceType: 'technical',
    status: 'current',
    lastVerified: '2026-01-15',
    relatedCompliance: ['SOC2_Type2'],
  },
  {
    id: 'auth-003',
    category: 'authentication',
    name: 'Multi-Factor Authentication',
    description: 'A tenant administrator can require everyone in the tenant to sign in with an authenticator app code as well as a password',
    implementation: 'Supabase Auth TOTP. Admin → Identity → Tenants & Workspaces → Sign-in turns it on (set_tenant_second_factor); the database then shows a session without the code nothing of that tenant\'s workspaces (migration 986). The web app and the iPhone app ask for the code, or set the authenticator up, right after sign-in.',
    evidenceType: 'technical',
    status: 'current',
    lastVerified: '2026-10-29',
    relatedCompliance: ['SOC2_Type2', 'ISO27001'],
  },
  
  // Authorization
  {
    id: 'authz-001',
    category: 'authorization',
    name: 'Role-Based Access Control',
    description: 'Granular permissions based on user roles within tenants',
    implementation: 'usePermissionCheck hook enforces role boundaries in UI and API',
    evidenceType: 'technical',
    status: 'current',
    lastVerified: '2026-01-20',
    relatedCompliance: ['SOC2_Type2', 'ISO27001'],
  },
  {
    id: 'authz-002',
    category: 'authorization',
    name: 'Tenant Isolation',
    description: 'Complete data separation between customer tenants',
    implementation: 'Supabase RLS policies enforce workspace_id boundaries on scoped tables; tenant used for tenant_memberships and tenant-level admin scope',
    evidenceType: 'technical',
    status: 'current',
    lastVerified: '2026-01-20',
    relatedCompliance: ['SOC2_Type2', 'ISO27001', 'GDPR'],
  },
  
  // Data Protection
  {
    id: 'data-001',
    category: 'data_protection',
    name: 'Encryption at Rest',
    description: 'All data encrypted at rest using AES-256',
    implementation: 'Supabase/PostgreSQL provides transparent data encryption',
    evidenceType: 'configuration',
    status: 'current',
    lastVerified: '2026-01-10',
    relatedCompliance: ['SOC2_Type2', 'ISO27001', 'GDPR', 'HIPAA'],
  },
  {
    id: 'data-002',
    category: 'data_protection',
    name: 'Encryption in Transit',
    description: 'All data encrypted in transit using TLS 1.2+',
    implementation: 'HTTPS enforced on all endpoints by Supabase infrastructure',
    evidenceType: 'configuration',
    status: 'current',
    lastVerified: '2026-01-10',
    relatedCompliance: ['SOC2_Type2', 'ISO27001', 'GDPR'],
  },
  {
    id: 'data-003',
    category: 'data_protection',
    name: 'Data Backup and Recovery',
    description: 'Automated daily backups with point-in-time recovery',
    implementation: 'Supabase provides automated backups with 30-day retention',
    evidenceType: 'configuration',
    status: 'current',
    lastVerified: '2026-01-10',
    relatedCompliance: ['SOC2_Type2', 'ISO27001'],
  },
  
  // Audit
  {
    id: 'audit-001',
    category: 'audit',
    name: 'Audit Logging',
    description: 'Comprehensive audit trail for security-relevant events',
    implementation: 'audit_events table captures auth, publish, and override actions',
    evidenceType: 'technical',
    status: 'current',
    lastVerified: '2026-01-20',
    relatedCompliance: ['SOC2_Type2', 'ISO27001', 'HIPAA'],
  },
  {
    id: 'audit-002',
    category: 'audit',
    name: 'Access Logging',
    description: 'Logging of all access attempts and authorization decisions',
    implementation: 'authTelemetry.ts records login success/failure events',
    evidenceType: 'technical',
    status: 'current',
    lastVerified: '2026-01-20',
    relatedCompliance: ['SOC2_Type2'],
  },
  
  // Infrastructure
  {
    id: 'infra-001',
    category: 'infrastructure',
    name: 'Cloud Infrastructure Security',
    description: 'Hosted on SOC 2 compliant cloud infrastructure',
    implementation: 'Deployed on Vercel (frontend) and Supabase (backend)',
    evidenceType: 'document',
    status: 'current',
    lastVerified: '2026-01-01',
    relatedCompliance: ['SOC2_Type2'],
  },
  {
    id: 'infra-002',
    category: 'infrastructure',
    name: 'DDoS Protection',
    description: 'Protection against distributed denial-of-service attacks',
    implementation: 'Vercel and Supabase provide edge-level DDoS protection',
    evidenceType: 'configuration',
    status: 'current',
    lastVerified: '2026-01-01',
    relatedCompliance: ['SOC2_Type2'],
  },
  
  // Availability
  {
    id: 'avail-001',
    category: 'availability',
    name: 'Managed High-Availability Infrastructure',
    description: 'Runs on Supabase (AWS) managed infrastructure with provider-level redundancy; a contractual uptime SLA is offered to enterprise customers (see docs/trust/sla.md)',
    implementation: 'Frontend on Vercel edge network; database and APIs on Supabase managed Postgres/Edge Functions (AWS us-east-1 by default)',
    evidenceType: 'configuration',
    status: 'current',
    lastVerified: '2026-06-15',
    relatedCompliance: ['SOC2_Type2'],
  },
];

// ============================================================================
// Security Contacts
// ============================================================================

export const SECURITY_CONTACTS: SecurityContact[] = [
  {
    role: 'Security Lead',
    name: 'Security Team',
    email: 'security@priorities.ai',
    responsibility: 'Overall security posture, incident response, policy',
  },
  {
    role: 'Data Protection Officer',
    name: 'DPO',
    email: 'privacy@priorities.ai',
    responsibility: 'GDPR/CCPA compliance, data subject requests, privacy',
  },
  {
    role: 'Compliance Manager',
    name: 'Compliance Team',
    email: 'compliance@priorities.ai',
    responsibility: 'SOC 2 readiness, compliance roadmap, vendor questionnaires',
  },
];

// ============================================================================
// Security Posture Functions
// ============================================================================

/**
 * Gets the current security posture.
 * This is the primary artifact for security reviews.
 */
export function getSecurityPosture(): SecurityPosture {
  const controls = SECURITY_CONTROLS;
  const currentControls = controls.filter(c => c.status === 'current');
  
  // Determine overall status
  let overallStatus: SecurityPosture['overallStatus'] = 'strong';
  if (currentControls.length < controls.length * 0.9) {
    overallStatus = 'acceptable';
  }
  if (currentControls.length < controls.length * 0.7) {
    overallStatus = 'needs_improvement';
  }
  
  // Build compliance status. Honest by design: SOC 2 and ISO 27001 are on the
  // roadmap (see docs/trust/soc2-controls-matrix.md and the enterprise security
  // page), not yet certified. GDPR/CCPA readiness is in progress via the Trust
  // Center documents (DPA, subprocessors, data retention) but not externally
  // attested. Do not upgrade any of these to 'current' without a real artifact.
  const complianceStatus: Record<ComplianceStandard, EvidenceStatus> = {
    SOC2_Type1: 'pending',
    SOC2_Type2: 'pending',
    GDPR: 'pending',
    CCPA: 'pending',
    HIPAA: 'not_applicable',
    ISO27001: 'pending',
  };
  
  return {
    version: '1.0.0',
    lastUpdated: new Date().toISOString().split('T')[0],
    overallStatus,
    controls,
    complianceStatus,
    contacts: SECURITY_CONTACTS,
  };
}

/**
 * Gets controls by category for focused review.
 */
export function getControlsByCategory(category: SecurityCategory): SecurityControl[] {
  return SECURITY_CONTROLS.filter(c => c.category === category);
}

/**
 * Gets controls related to a specific compliance standard.
 */
export function getControlsByCompliance(standard: ComplianceStandard): SecurityControl[] {
  return SECURITY_CONTROLS.filter(c => c.relatedCompliance?.includes(standard));
}

/**
 * Checks if security posture meets a minimum threshold.
 * Used to validate J2.2 (acceptance does not require ad hoc trust exceptions).
 */
export function meetsSecurityThreshold(
  requiredCategories: SecurityCategory[] = ['authentication', 'authorization', 'data_protection']
): { passed: boolean; gaps: string[] } {
  const gaps: string[] = [];
  
  for (const category of requiredCategories) {
    const categoryControls = getControlsByCategory(category);
    const currentControls = categoryControls.filter(c => c.status === 'current');
    
    if (currentControls.length === 0) {
      gaps.push(`No current controls for ${category}`);
    } else if (currentControls.length < categoryControls.length * 0.5) {
      gaps.push(`Less than 50% of ${category} controls are current`);
    }
  }
  
  return {
    passed: gaps.length === 0,
    gaps,
  };
}

/**
 * Generates a security summary for customer review.
 * This is the artifact that security owners review (J2.1).
 */
export function generateSecuritySummary(): string {
  const posture = getSecurityPosture();
  const currentCount = posture.controls.filter(c => c.status === 'current').length;
  const totalCount = posture.controls.length;
  
  const lines = [
    `# Peak Priorities Security Posture Summary`,
    ``,
    `**Version:** ${posture.version}`,
    `**Last Updated:** ${posture.lastUpdated}`,
    `**Overall Status:** ${posture.overallStatus.toUpperCase()}`,
    ``,
    `## Control Coverage`,
    ``,
    `${currentCount} of ${totalCount} controls are current (${Math.round(currentCount / totalCount * 100)}%)`,
    ``,
    `## Compliance Status`,
    ``,
  ];
  
  for (const [standard, status] of Object.entries(posture.complianceStatus)) {
    lines.push(`- **${standard}:** ${status}`);
  }
  
  lines.push(``);
  lines.push(`## Security Contacts`);
  lines.push(``);
  
  for (const contact of posture.contacts) {
    lines.push(`- **${contact.role}:** ${contact.email}`);
  }
  
  lines.push(``);
  lines.push(`## Categories`);
  lines.push(``);
  
  const categories: SecurityCategory[] = ['authentication', 'authorization', 'data_protection', 'audit', 'infrastructure', 'availability'];
  for (const category of categories) {
    const controls = getControlsByCategory(category);
    const current = controls.filter(c => c.status === 'current').length;
    lines.push(`- **${category}:** ${current}/${controls.length} controls current`);
  }
  
  return lines.join('\n');
}

/**
 * Gets the security decision owner for a customer.
 * This satisfies J2.1.2 (customer can identify the right security decision owner).
 */
export function getSecurityDecisionOwner(): SecurityContact {
  return SECURITY_CONTACTS.find(c => c.role === 'Security Lead') || SECURITY_CONTACTS[0];
}


